import { Theme } from '@yandex-int/k-common/typings';
import cx from 'classnames';
import { Select, SelectEvent, Text } from 'platform-components';
import { SELECT_THEMES } from 'platform-components/constants';
// @ts-ignore
import { Route, Switch } from 'platform-components/src/components/router/router';
import { withRouterPath, InjectedProps as WithRouterPathProps } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { connect } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';

import * as userActions from 'common.components/user/actions';

import RootTheme from './__root-theme/sidebar-themes__root-theme';

import './sidebar-themes.scss';

const ACTUAL_ID = 'actual';
const ACTUAL_KEY = 'actual-themes';

interface NavItem {
    id: number | typeof ACTUAL_ID;
    key: number | typeof ACTUAL_KEY;
    name: string;
    selected?: boolean;
    to: string;
}

interface OwnProps {
    isMobile?: boolean;
    themes: Array<Theme>;
    shouldAddActualThemes?: boolean;
    mix?: string;
}

interface HOCProps extends WithRouterPathProps, WrappedComponentProps {}

interface DispatchProps {
    setWizardVariables: typeof userActions.setWizardVariables;
}

interface Props extends OwnProps, DispatchProps, HOCProps {}

export class SidebarThemes extends React.Component<Props> {
    static contextTypes = {
        staticUrls: PropTypes.object.isRequired,
    };
    context!: React.ContextType<React.Context<{ staticUrls: Record<string, string> }>>;

    getSubjectThemePath = (themeId: number) => {
        const {
            getRouterPath,
            match: { params },
            location: { search },
        } = this.props;
        const { staticUrls } = this.context;
        const path = params?.classId ? staticUrls.labLibrarySubjectTheme : staticUrls.deepLabTheme;

        return `${getRouterPath(path, { themeId })}${search}`;
    };

    getActualThemePath = () => {
        const { getRouterPath } = this.props;
        const { staticUrls } = this.context;

        return getRouterPath(staticUrls.labLibraryActualThemes);
    };

    handleSelectTheme = (option: SelectEvent<number | typeof ACTUAL_ID | null>) => {
        const { history } = this.props;

        const {
            target: { value: themeId },
        } = option;

        if (!themeId) {
            return;
        }

        const path = themeId === ACTUAL_ID ? this.getActualThemePath() : this.getSubjectThemePath(themeId);

        history.push(path);
    };

    getThemesNavItems = (isOnActualThemesPage: boolean): Array<NavItem> => {
        const {
            intl,
            match: { params },
            shouldAddActualThemes,
            themes,
        } = this.props;
        const themeId = Number(params.themeId);

        const navItems: Array<NavItem> = themes.map(({ id, name }) => ({
            id,
            key: id,
            name,
            selected: id === themeId,
            to: this.getSubjectThemePath(id),
        }));

        if (shouldAddActualThemes) {
            navItems.push({
                id: ACTUAL_ID,
                key: ACTUAL_KEY,
                name: intl.formatMessage({ id: 'library.actualThemes' }),
                selected: isOnActualThemesPage,
                to: this.getActualThemePath(),
            });
        }

        return navItems;
    };

    getContent = (isOnActualThemesPage: boolean) => {
        const {
            intl,
            match: { params },
            mix,
            isMobile,
        } = this.props;
        const themeId = Number(params.themeId);

        const themeNavItems = this.getThemesNavItems(isOnActualThemesPage);

        if (isMobile) {
            return (
                <Select
                    mix="sidebar-themes__select"
                    onSelect={(option) => this.handleSelectTheme(option)}
                    placeholder={intl.formatMessage({ id: 'library.themeSelect.placeholder' })}
                    theme={SELECT_THEMES.TEACHER}
                    value={themeId || null}
                    width="100%"
                >
                    {themeNavItems.map(({ id, name }) => (
                        <Select.Option key={id} value={id}>
                            <Text ellipsis type={Text.TYPE.MARKDOWN}>
                                {name}
                            </Text>
                        </Select.Option>
                    ))}
                </Select>
            );
        }

        return (
            <div className={cx(mix, 'sidebar-themes')}>
                {themeNavItems.map((theme) => (
                    <RootTheme
                        id={theme.id}
                        key={theme.key}
                        name={theme.name}
                        selected={theme.selected}
                        to={theme.to}
                    />
                ))}
            </div>
        );
    };

    render = () => {
        const { staticUrls } = this.context;

        return (
            <Switch>
                <Route exact path={staticUrls.labLibraryActualThemes} render={() => this.getContent(true)} />
                <Route render={() => this.getContent(false)} />
            </Switch>
        );
    };
}

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    const { setWizardVariables } = userActions;

    return bindActionCreators({ setWizardVariables }, dispatch);
};

const component = injectIntl(withRouterPath(connect(null, mapDispatchToProps)(SidebarThemes)));

component.displayName = 'SidebarThemes';

export default component;
